import { FC, useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Icon, Modal } from 'src/components';
import { RemindersApi, TrackingApi } from 'src/api';
import type { ReminderType } from 'src/api/reminders/types';
import { RemindersTrackingAction } from 'src/api/tracking/types';
import { useUserContext } from 'src/context/user';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { addToRemindersScreenStorageEntry } from './remindersStorageEntry';
import * as S from './styles';

export type ReminderOption = ReminderType;

const msgs = defineMessages({
  title: {
    id: 'reminders_screen_title',
    defaultMessage: 'Come back before your memory fades',
  },
  description: {
    id: 'reminders_screen_description',
    defaultMessage: 'When should we nudge you?',
  },
  fourHoursTitle: {
    id: 'reminders_screen_four_hours_title',
    defaultMessage: 'Remind me in 4 hours',
  },
  fourHoursDescription: {
    id: 'reminders_screen_four_hours_description',
    defaultMessage: 'Best time for your first review.',
  },
  fourHoursBadge: {
    id: 'reminders_screen_four_hours_badge',
    defaultMessage: 'Recommended',
  },
  tomorrowTitle: {
    id: 'reminders_screen_tomorrow_title',
    defaultMessage: 'Tomorrow',
  },
  tomorrowDescription: {
    id: 'reminders_screen_tomorrow_description',
    defaultMessage: 'Same time, once a day.',
  },
  tip: {
    id: 'reminders_screen_tip',
    defaultMessage:
      'Our smart spaced repetition system calculates when you should review a word and prompts you at the right time. Answer correctly and the gap until you see it again gets longer.',
  },
  skip: {
    id: 'reminders_screen_skip',
    defaultMessage: 'Set notifications later',
  },
});

export type RemindersScreenProps = {
  onSelect: (option: ReminderOption) => void;
  onSkip: () => void;
};

export const RemindersScreen: FC<RemindersScreenProps> = ({ onSelect, onSkip }) => {
  const intl = useIntl();
  const { user } = useUserContext();
  const languagePairId = getLanguagePairId();

  const trackAction = useCallback(
    (action: RemindersTrackingAction) => {
      void TrackingApi.trackUserAction(action, 'reminders_screen', languagePairId, !!user?.isPro);
    },
    [languagePairId, user?.isPro],
  );

  useEffect(() => {
    addToRemindersScreenStorageEntry(user);
    trackAction('screen_viewed');
    // Only ever runs once, when the screen mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (option: ReminderOption) => {
    trackAction(option === 'four_hours' ? 'clicked_remind_in_4_hours' : 'clicked_remind_tomorrow');
    void RemindersApi.createReminder(option);
    onSelect(option);
  };

  const handleSkip = () => {
    trackAction('clicked_set_notifications_later');
    onSkip();
  };

  return (
    <Modal isOpen fullSize hideCloseButton handleClose={handleSkip}>
      <S.ContentOuterContainer>
        <S.ContentInnerContainer data-testid="reminders-screen">
          <S.IconStage>
            <Icon name="timer" />
          </S.IconStage>

          <S.Title>{intl.formatMessage(msgs.title)}</S.Title>
          <S.Description>{intl.formatMessage(msgs.description)}</S.Description>

          <S.OptionsList>
            <S.OptionButton $isRecommended onClick={() => handleSelect('four_hours')}>
              <S.OptionIcon $isRecommended>
                <Icon name="timer" />
              </S.OptionIcon>
              <S.OptionTextWrapper>
                <S.OptionTitle>{intl.formatMessage(msgs.fourHoursTitle)}</S.OptionTitle>
                <S.OptionDescription>
                  {intl.formatMessage(msgs.fourHoursDescription)}
                </S.OptionDescription>
              </S.OptionTextWrapper>
              <S.RecommendedBadge>{intl.formatMessage(msgs.fourHoursBadge)}</S.RecommendedBadge>
            </S.OptionButton>

            <S.OptionButton onClick={() => handleSelect('tomorrow')} $isRecommended={false}>
              <S.OptionIcon $isRecommended={false}>
                <Icon name="bell" />
              </S.OptionIcon>
              <S.OptionTextWrapper>
                <S.OptionTitle>{intl.formatMessage(msgs.tomorrowTitle)}</S.OptionTitle>
                <S.OptionDescription>
                  {intl.formatMessage(msgs.tomorrowDescription)}
                </S.OptionDescription>
              </S.OptionTextWrapper>
            </S.OptionButton>
          </S.OptionsList>

          <S.TipBox>
            <S.TipIcon>
              <Icon name="idea" />
            </S.TipIcon>
            <S.TipText>{intl.formatMessage(msgs.tip)}</S.TipText>
          </S.TipBox>

          <S.SkipButton type="button" onClick={handleSkip}>
            {intl.formatMessage(msgs.skip)}
          </S.SkipButton>
        </S.ContentInnerContainer>
      </S.ContentOuterContainer>
    </Modal>
  );
};
